import { createContext, useContext, useEffect, useMemo, useState } from "react"

/**
 * The FocusStackContext provides a way to manage multiple focus traps in a stack-like manner.
 * This is useful for scenarios where multiple modals or popups may be open at the same time,
 * ensuring that focus is correctly trapped within the topmost element.
 */
export const FocusStackContext = createContext<{
  insertFocusTrap: (trap: (e: FocusEvent) => void) => void
  removeFocusTrap: (trap: (e: FocusEvent) => void) => void
} | null>(null)

/**
 * Refocuses the first focusable child element within the given parent element.
 */
export const refocusChildren = (parent: HTMLDivElement) => {
  if (!parent) {
    return
  }

  const focusables = parent.querySelectorAll('button, [href], input, select, textarea, li, a, [tabindex="0"]')
  if (focusables && focusables.length > 0) {
    for (let i = 0; i < focusables.length; i++) {
      const el = focusables[i] as HTMLElement
      const isFocusable =
        !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true" && el.style.display !== "none"

      if (isFocusable && document.activeElement !== el) {
        el.focus()
        break
      }
    }
  }
}

/**
 * Creates a focus trap function for the given element. Also stops all propagation of the focus element to any other handlers that may be registered.
 */
export const createFocusTrap = (el: HTMLDivElement | null) => (e: FocusEvent) => {
  if (!el) {
    return
  }

  if (e) {
    e.stopImmediatePropagation()
    e.preventDefault()

    if (el.contains(e.target as Node)) {
      return
    }
  }
  refocusChildren(el)
}

/**
 * Hook to manager focus traps in a stack-like manner.
 * If no stack has been provided via context, a new stack is created.
 * Once inside the stacked context components can register and unregister focus traps. The latest registered trap is the active one.
 * This ensures there is only ever one global focus handler active on the document, which makes it much easier to handle the logic of multiple modals/popups with focus traps.
 */
export const useFocusStack = () => {
  const focusContext = useContext(FocusStackContext)
  const [focusStack, setFocusStack] = useState<Array<(e: FocusEvent) => void>>([])

  useEffect(() => {
    if (focusContext) {
      return
    }

    const focusTrap = (e: FocusEvent) => {
      if (focusStack.length === 0) {
        return
      }
      const topTrap = focusStack[0]
      topTrap(e)
    }

    document.addEventListener("focus", focusTrap, {
      capture: true,
    })
    focusTrap(null)

    return () => {
      document.removeEventListener("focus", focusTrap, {
        capture: true,
      })
    }
  }, [focusContext, focusStack])

  const newFocusContext = useMemo(
    () => ({
      insertFocusTrap: (trap: (e: FocusEvent) => void) => {
        setFocusStack((prev) => {
          const newStack = [trap, ...prev]
          return newStack
        })
      },
      removeFocusTrap: (trap: (e: FocusEvent) => void) => {
        setFocusStack((prev) => {
          const newStack = prev.filter((t) => t !== trap)
          return newStack
        })
      },
    }),
    [],
  )

  if (focusContext) {
    return focusContext
  }

  return newFocusContext
}
